import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Button } from '../atoms';
import { PageHeader } from '../shell';
import { DataTable, Pagination, ListToolbar, SavedViewMenu } from '../organisms';
import { useServerList } from '../lib/useServerList';
import { US_STATE_OPTIONS } from '../lib/us-states';

/**
 * PropertiesList — /v2/properties
 *
 * Server-side search / sort / paginate (PRD 06.1): the debounced search box,
 * the status / state / client filters, the sortable column headers, and the
 * pager drive `GET /api/properties?q&status&state&client_id&sort&dir&page&limit`
 * (org + SoD row-scope enforced server-side). The `state` scalar filter + the
 * `client_id` FK filter are honored by the generic-CRUD list handler.
 *
 * Filter-by-client + clickable state (PRD 2.2): the Client Select narrows to one
 * client's portfolio; the state token in the Location column is a button that
 * applies the state filter in a single click. Searchable state (PRD 2.3): `state`
 * is now a properties SEARCH_COLUMN, so typing "TX" surfaces every Texas
 * property. Saved views (PRD 2.5): the SavedViewMenu persists & restores the full
 * {q, status, state, client_id, group_label, sort, dir} envelope against /api/saved-views.
 *
 * Phase 11 Block 8 PR 2 (2026-05-12); rewired for server-side list (06.1) +
 * client/state filters + saved views (2.2 / 2.3 / 2.5).
 */

interface Property {
  id: string;
  name: string;
  client_id: string;
  city: string | null;
  state: string | null;
  group_label?: string | null;
  property_type: string | null;
  re_pp: 'real_estate' | 'personal_property' | 'real_and_personal';
  status: 'active' | 'inactive' | 'in_setup' | 'historical' | 'sold' | 'sold_after_value' | 'unknown';
  year: number;
}

interface Client {
  id: string;
  name: string;
}

const statusVariant: Record<Property['status'], 'success' | 'warning' | 'neutral' | 'info'> = {
  active: 'success', inactive: 'neutral', in_setup: 'warning', historical: 'info',
  sold: 'neutral', sold_after_value: 'neutral', unknown: 'neutral',
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'in_setup', label: 'In Setup' },
  { value: 'historical', label: 'Historical' },
  { value: 'sold', label: 'Sold' },
];

// Assignment scope (dashboard "My Properties" deep-link): ?assigned=me narrows to
// the requesting user's own book via agentScopeClause (see ParcelsList).
const ASSIGNED_OPTIONS = [
  { value: 'me', label: 'Assigned to me' },
];

// Roles that may create org-scoped ("shared") saved views (admin-tier, matching
// the saved-views router's ADMIN_TIER_LEVEL). Also gates the shared checkbox.
const ADMIN_TIER = new Set(['super_admin', 'admin', 'administrator']);

interface CurrentUser { id: string; role: string }

export function PropertiesList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Deep-link filters, read once at mount (the toolbar owns them thereafter) so a
  // dashboard KPI card lands on the SAME set it counted — ?status=in_setup is what
  // the "Properties in setup" card links to.
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '');
  const [stateFilter, setStateFilter] = useState(() => searchParams.get('state') ?? '');
  const [clientFilter, setClientFilter] = useState(() => searchParams.get('client_id') ?? '');
  // "My Properties" deep-link: ?assigned=me scopes to my book (read once at mount).
  const [assignedFilter, setAssignedFilter] = useState(() => searchParams.get('assigned') ?? '');
  // Spec 0011: the property's group label (exact match server-side).
  const [groupFilter, setGroupFilter] = useState(() => searchParams.get('group_label') ?? '');

  const list = useServerList<Property>({
    resource: 'properties',
    queryKey: ['properties-list'],
    defaultPageSize: 25,
    defaultSort: { id: 'name', desc: false },
    filters: { status: statusFilter, state: stateFilter, client_id: clientFilter, assigned: assignedFilter, group_label: groupFilter },
  });

  // The group labels on the caller's book, for the Group filter.
  const groupLabelsQuery = useQuery<string[]>({
    queryKey: ['property-group-labels'],
    queryFn: ({ signal }) => api.get<string[]>('/api/property-group-labels', signal),
    staleTime: 60_000,
  });
  const groupOptions = useMemo(
    () => (groupLabelsQuery.data ?? []).map((g) => ({ value: g, label: g })),
    [groupLabelsQuery.data],
  );

  // Client names for the join column. Fetched once (bare list) and cached
  // separately from the paged property list; also feeds the Client filter Select.
  const clientsQuery = useQuery<Client[]>({
    queryKey: ['clients-name-map'],
    queryFn: ({ signal }) => api.get<Client[]>('/api/clients', signal),
  });

  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of (clientsQuery.data ?? [])) map.set(c.id, c.name);
    return map;
  }, [clientsQuery.data]);

  const clientOptions = useMemo(
    () => (clientsQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [clientsQuery.data],
  );

  // Current user powers the SavedViewMenu's share/delete affordances.
  const meQuery = useQuery<CurrentUser>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUser>('/api/current-user', signal),
    staleTime: 5 * 60_000,
  });
  const canShare = ADMIN_TIER.has(meQuery.data?.role ?? '');

  // The filter/sort envelope a saved view stores; applying a view writes these
  // back onto the server-side list state. Must enumerate EVERY filter the page
  // uses (q, status, state, client_id, sort, dir) so an applied view restores all.
  const currentConfig = useMemo(
    () => ({
      q: list.searchInput,
      status: statusFilter,
      state: stateFilter,
      client_id: clientFilter,
      assigned: assignedFilter,
      group_label: groupFilter,
      sort: list.sorting[0]?.id ?? 'name',
      dir: list.sorting[0]?.desc ? 'desc' : 'asc',
    }),
    [list.searchInput, statusFilter, stateFilter, clientFilter, assignedFilter, groupFilter, list.sorting],
  );

  function applyView(config: Record<string, unknown>) {
    list.setSearchInput(typeof config.q === 'string' ? config.q : '');
    setStatusFilter(typeof config.status === 'string' ? config.status : '');
    setStateFilter(typeof config.state === 'string' ? config.state : '');
    setClientFilter(typeof config.client_id === 'string' ? config.client_id : '');
    setAssignedFilter(typeof config.assigned === 'string' ? config.assigned : '');
    setGroupFilter(typeof config.group_label === 'string' ? config.group_label : '');
    const sortId = typeof config.sort === 'string' ? config.sort : 'name';
    list.setSorting([{ id: sortId, desc: config.dir === 'desc' }]);
    // useServerList resets to page 1 whenever search/filter/sort change.
  }

  const columns = useMemo<ColumnDef<Property>[]>(() => [
    { accessorKey: 'name', header: 'Name' },
    {
      id: 'client',
      header: 'Client',
      enableSorting: false,
      cell: ({ row }) => clientNameById.get(row.original.client_id) || '—',
    },
    {
      id: 'location',
      header: 'Location',
      enableSorting: false,
      cell: ({ row }) => {
        const { city, state } = row.original;
        if (!city && !state) return '—';
        return (
          <span>
            {city ? <span>{city}</span> : null}
            {city && state ? <span>, </span> : null}
            {state ? (
              // Clickable state token → applies the state filter in one click
              // (PRD 2.2). stopPropagation is mandatory: the row <tr> onClick
              // navigates to the property detail (DataTable renders rows clickable).
              <button
                type="button"
                aria-label={`Filter properties by state ${state}`}
                onClick={(e) => { e.stopPropagation(); setStateFilter(state); }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  font: 'inherit',
                  color: 'var(--brand-primary)',
                  cursor: 'pointer',
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
              >
                {state}
              </button>
            ) : null}
          </span>
        );
      },
    },
    { accessorKey: 'group_label', header: 'Group', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'property_type', header: 'Type', cell: ({ getValue }) => (getValue() as string) || '—' },
    {
      accessorKey: 're_pp',
      header: 'RE/PP',
      cell: ({ getValue }) => {
        const v = getValue() as Property['re_pp'];
        return v === 'real_estate' ? 'RE' : v === 'personal_property' ? 'PP' : 'RE + PP';
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const s = String(getValue() ?? 'active') as Property['status'];
        return <Badge variant={statusVariant[s] ?? 'neutral'} dot>{s.replace(/_/g, ' ')}</Badge>;
      },
    },
  ], [clientNameById]);

  const { query, rows, total } = list;
  const hasFilters = list.search !== '' || statusFilter !== '' || stateFilter !== '' || clientFilter !== '' || assignedFilter !== '' || groupFilter !== '';

  return (
    <>
      <PageHeader
        title="Properties"
        subtitle={query.isLoading ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'property' : 'properties'}`}
        actions={
          <>
            <SavedViewMenu
              entityType="properties"
              currentConfig={currentConfig}
              onApply={applyView}
              canShare={canShare}
              currentUserId={meQuery.data?.id}
            />
            <Button variant="primary" size="md" onClick={() => navigate('/properties/new')}>
              + New property
            </Button>
          </>
        }
      />

      <ListToolbar
        searchValue={list.searchInput}
        onSearchChange={list.setSearchInput}
        searchPlaceholder="Search properties by name, address, city, state (TX)…"
        filters={[
          {
            key: 'status',
            value: statusFilter,
            onChange: setStatusFilter,
            placeholder: 'All statuses',
            options: STATUS_OPTIONS,
          },
          {
            key: 'state',
            value: stateFilter,
            onChange: setStateFilter,
            placeholder: 'All states',
            options: US_STATE_OPTIONS,
            widthPx: 150,
          },
          {
            key: 'client_id',
            value: clientFilter,
            onChange: setClientFilter,
            placeholder: 'All clients',
            options: clientOptions,
            widthPx: 240,
          },
          {
            key: 'assigned',
            value: assignedFilter,
            onChange: setAssignedFilter,
            placeholder: 'Any assignment',
            options: ASSIGNED_OPTIONS,
          },
          {
            key: 'group_label',
            value: groupFilter,
            onChange: setGroupFilter,
            placeholder: 'All groups',
            options: groupOptions,
            widthPx: 200,
          },
        ]}
      />

      {query.error && (
        <ErrorBanner message={(query.error as Error).message} />
      )}
      {!query.isLoading && total === 0 && !query.error && (
        hasFilters ? <NoMatches /> : <EmptyState />
      )}
      {(total > 0 || query.isFetching) && !query.error && (
        <>
          <DataTable
            data={rows}
            columns={columns}
            sorting={list.sorting}
            onSortingChange={list.setSorting}
            onRowClick={(row) => navigate(`/properties/${row.id}`)}
            // Warm the property DETAIL query (PropertyDetail: ['property', id] →
            // /api/properties/:id) on row hover so the click opens a cached page.
            onRowPrefetch={(row) => {
              void queryClient.prefetchQuery({
                queryKey: ['property', row.id],
                queryFn: ({ signal }) => api.get<Property>(`/api/properties/${row.id}`, signal),
              });
            }}
            emptyMessage={query.isFetching ? 'Loading…' : 'No properties'}
          />
          <Pagination
            pageIndex={list.pageIndex}
            pageCount={list.pageCount}
            pageSize={list.pageSize}
            totalRows={total}
            onPageChange={list.setPageIndex}
            onPageSizeChange={list.setPageSize}
          />
        </>
      )}
    </>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-4)',
      background: 'var(--color-status-red-bg)',
      border: '1px solid var(--color-status-red)',
      color: 'var(--color-status-red)',
      borderRadius: 'var(--r-lg)',
      marginBottom: 'var(--sp-4)',
    }}>
      <strong>Failed to load properties:</strong> {message}
    </div>
  );
}

function NoMatches() {
  return (
    <div style={{
      padding: 'var(--sp-7)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
    }}>
      <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>No properties match your search or filters.</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{
      padding: 'var(--sp-8)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
    }}>
      <h2 style={{
        margin: '0 0 var(--sp-2)',
        fontSize: 'var(--fs-xl)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--color-ink-3)',
      }}>No properties yet</h2>
      <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>
        Use "+ New property" or the v1 wizard to create your first property.
      </p>
    </div>
  );
}
